/* ==========================================================================
   XELPHORA — facets.js
   <collection-facets> — Ajax filtering, sorting and pagination on top of
   Shopify's Storefront Filtering.

   The markup underneath already works without this file: the facet form is a
   GET form to the collection URL and pagination is real anchors. Everything
   here is an upgrade, not a requirement.

   Two things this does that naive implementations skip:
   - It re-renders the facet list too, not just the grid. Counts and
     availability change with every selection; leaving the old ones on screen
     is how filter UIs end up lying about how many results a checkbox has.
   - It restores keyboard focus after that swap. Replacing the sidebar out from
     under someone tabbing through it is the fastest way to make an accessible
     filter unusable.
   ========================================================================== */

(() => {
  'use strict';

  const Theme = (window.Xelphora = window.Xelphora || {});

  // Resolved per instance rather than at module load: this file is a
  // section-level script, and belt-and-braces beats depending on tag order.
  const utils = () => Theme.utils || {};

  const MOBILE = '(max-width: 989px)';
  const isMobile = () => window.matchMedia(MOBILE).matches;

  class CollectionFacets extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.anchor = this.dataset.resultsAnchor;
      this.results = this.querySelector('[data-collection-results]');
      this.facetsHost = this.querySelector('[data-facets]');
      this.panel = this.querySelector('[data-facets-panel]');
      this.toggle = this.querySelector('[data-facets-toggle]');
      this.abort = null;

      const { debounce, FocusTrap, ScrollLock } = utils();
      this.debounce = debounce;
      this.FocusTrap = FocusTrap;
      this.ScrollLock = ScrollLock;

      this.bindForm();
      this.bindLinks();
      this.bindPanel();
      this.bindShowMore();

      this.onPopState = this.onPopState.bind(this);
      window.addEventListener('popstate', this.onPopState);
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPopState);
      this.abort?.abort();
      if (this.panelOpen) this.closePanel();
    }

    get form() {
      // Re-queried every time: the form is replaced on each facet refresh.
      return this.querySelector('[data-facet-form]');
    }

    /* --------------------------------------------------------------- form */
    bindForm() {
      // Checkboxes and the sort select apply immediately. Price inputs are
      // debounced, because a shopper types "150" as three separate events.
      this.addEventListener('change', (event) => {
        const target = event.target;
        if (target.closest('[data-facet-price]')) return;
        if (!target.closest('[data-facet-form]') && !target.matches('[data-sort-select]')) return;
        this.apply();
      });

      const debouncedApply = this.debounce
        ? this.debounce(() => this.apply(), 700)
        : () => this.apply();
      this.addEventListener('input', (event) => {
        if (event.target.matches('[data-facet-price]')) debouncedApply();
      });

      // Enter inside the form should filter in place, not reload the page.
      this.addEventListener('submit', (event) => {
        if (!event.target.matches('[data-facet-form]')) return;
        event.preventDefault();
        this.apply();
      });
    }

    /** Serialises the form — including the sort select, which lives outside it
     *  and is associated via form="…" — and drops empty values so the URL
     *  stays readable and shareable. */
    buildSearchParams() {
      const form = this.form;
      if (!form) return '';

      const params = new URLSearchParams();
      new FormData(form).forEach((value, key) => {
        if (typeof value === 'string' && value.trim() === '') return;
        params.append(key, value);
      });
      return params.toString();
    }

    apply() {
      const query = this.buildSearchParams();
      const url = `${window.location.pathname}${query ? `?${query}` : ''}`;
      // A filter change is a navigable state, so it gets a history entry —
      // Back should undo the last filter, not leave the collection entirely.
      this.navigate(url, { push: true });
    }

    /* -------------------------------------------------------------- links */
    bindLinks() {
      this.addEventListener('click', (event) => {
        const link = event.target.closest(
          '[data-facet-link], [data-facet-clear-all], [data-pagination-link]'
        );
        if (!link || link.target === '_blank' || event.metaKey || event.ctrlKey) return;

        event.preventDefault();
        this.navigate(link.getAttribute('href'), {
          push: true,
          scroll: link.matches('[data-pagination-link]'),
        });
      });
    }

    /* -------------------------------------------------------------- panel */
    bindPanel() {
      if (!this.panel || !this.toggle) return;

      this.trap = this.FocusTrap ? new this.FocusTrap(this.panel) : null;
      this.panelOpen = false;

      this.toggle.addEventListener('click', () => this.openPanel());

      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-facets-close]')) this.closePanel();
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.panelOpen) this.closePanel();
      });

      // Crossing the breakpoint while the off-canvas panel is open would leave
      // the page scroll-locked behind a sidebar that is no longer a dialog.
      this.mq = window.matchMedia(MOBILE);
      this.mq.addEventListener('change', (e) => {
        if (!e.matches && this.panelOpen) this.closePanel();
      });
    }

    openPanel() {
      if (!isMobile() || this.panelOpen) return;
      this.panelOpen = true;
      this.panel.classList.add('is-open');
      this.toggle.setAttribute('aria-expanded', 'true');
      this.ScrollLock?.lock();
      this.trap?.activate();
    }

    closePanel() {
      if (!this.panelOpen) return;
      this.panelOpen = false;
      this.panel.classList.remove('is-open');
      this.toggle.setAttribute('aria-expanded', 'false');
      this.ScrollLock?.unlock();
      this.trap?.deactivate();
    }

    /* ---------------------------------------------------------- show more */
    bindShowMore() {
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-facet-more]');
        if (!button) return;

        const list = button.previousElementSibling;
        if (!list?.matches('[data-facet-values]')) return;

        const expanded = button.getAttribute('aria-expanded') === 'true';
        list.querySelectorAll('[data-facet-overflow]').forEach((li) => {
          li.toggleAttribute('hidden', expanded);
        });

        button.setAttribute('aria-expanded', String(!expanded));
        button.textContent = expanded ? button.dataset.labelMore : button.dataset.labelLess;
      });
    }

    /* ----------------------------------------------------------- navigate */
    onPopState() {
      // Back/forward: re-render from the URL, but do not push another entry.
      this.navigate(window.location.href, { push: false });
    }

    async navigate(url, { push = true, scroll = false } = {}) {
      const target = new URL(url, window.location.origin);
      const fetchUrl = new URL(target);
      fetchUrl.searchParams.set('section_id', this.sectionId);

      this.abort?.abort();
      this.abort = new AbortController();
      this.setBusy(true);

      // Remember what had focus so it can be handed back after the swap.
      const activeId = document.activeElement?.id || null;

      try {
        const response = await fetch(fetchUrl, { signal: this.abort.signal });
        if (!response.ok) throw new Error(response.statusText);

        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');

        this.swap(doc, '[data-collection-results]');
        this.swap(doc, '[data-facets]');
        this.swap(doc, '[data-product-count]');
        this.syncActiveCount(doc);

        if (push) {
          window.history.pushState({ facets: true }, '', target.toString());
        }

        Theme.reveal?.scan(this.results || this);
        this.restoreFocus(activeId);
        if (scroll) this.scrollToResults();
      } catch (error) {
        if (error.name === 'AbortError') return;
        // Any failure falls back to a real navigation rather than leaving the
        // shopper looking at results that do not match the controls.
        window.location.href = target.toString();
      } finally {
        if (!this.abort?.signal.aborted) this.setBusy(false);
      }
    }

    swap(doc, selector) {
      const fresh = doc.querySelector(selector);
      const current = this.querySelector(selector);
      if (fresh && current) current.innerHTML = fresh.innerHTML;
    }

    /** The mobile toggle carries a count badge that the server rebuilds. */
    syncActiveCount(doc) {
      if (!this.toggle) return;
      const fresh = doc.querySelector('[data-active-count]');
      const current = this.toggle.querySelector('[data-active-count]');

      if (fresh && current) current.textContent = fresh.textContent;
      else if (fresh && !current) this.toggle.appendChild(fresh.cloneNode(true));
      else if (!fresh && current) current.remove();
    }

    restoreFocus(id) {
      if (!id) return;
      const node = this.querySelector(`#${window.CSS?.escape ? CSS.escape(id) : id}`);
      if (node) node.focus({ preventScroll: true });
    }

    scrollToResults() {
      const node = this.anchor ? document.getElementById(this.anchor) : this.results;
      node?.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'start',
      });
    }

    setBusy(busy) {
      this.results?.setAttribute('aria-busy', String(busy));
      this.toggleAttribute('data-loading', busy);
    }
  }

  if (!customElements.get('collection-facets')) {
    customElements.define('collection-facets', CollectionFacets);
  }
})();
